import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { changePassword } from '../services/api';
import PasswordInput from './PasswordInput';
import Button from './ui/Button';
import Modal from './ui/Modal';
import logoActivus from '../assets/ActivusSoftware.svg';

// roles : rôles qui voient l'entrée (DO-208) ; absent = ADMIN et MANAGER.
const NAV = [
  { to: '/', label: '📊 Dashboard', end: true },
  { to: '/licenses', label: '🔑 Licences' },
  { to: '/instances', label: '🖥️ Instances', roles: ['ADMIN', 'MANAGER', 'PUSH_AGENT'] },
  { to: '/attempts', label: '🚪 Tentatives' },
  { to: '/packages', label: '📦 Packages', roles: ['ADMIN', 'MANAGER', 'PUSH_AGENT'] },
  { to: '/users', label: '👥 Utilisateurs', roles: ['ADMIN'] },
];
const DEFAULT_ROLES = ['ADMIN', 'MANAGER'];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [pwdModal, setPwdModal] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [pwdOk, setPwdOk] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const closePwdModal = () => {
    setPwdModal(false);
    setCurrent(''); setNext(''); setConfirm('');
    setPwdError(''); setPwdOk(false);
  };

  const handleChangePassword = async e => {
    e.preventDefault();
    setPwdError(''); setPwdOk(false);
    if (next.length < 8) { setPwdError('Le nouveau mot de passe doit faire au moins 8 caractères.'); return; }
    if (next !== confirm) { setPwdError('La confirmation ne correspond pas.'); return; }
    try {
      await changePassword(current, next);
      setPwdOk(true);
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      setPwdError(err.response?.data?.error || 'Échec du changement de mot de passe.');
    }
  };

  return (
    <div style={styles.shell}>
      <aside style={styles.sidebar}>
        <div style={styles.logoBox}>
          <img src={logoActivus} alt="Activus Group" style={styles.logoImg} />
        </div>
        <div style={styles.appName}>License Monitor</div>
        <nav>
          {NAV.filter(n => (n.roles || DEFAULT_ROLES).includes(user?.role)).map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              style={({ isActive }) => ({ ...styles.link, ...(isActive ? styles.active : {}) })}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div style={styles.userBlock}>
          <span style={styles.userName}>👤 {user?.username}</span>
          <Button variant="sidebar" size="sm" onClick={() => setPwdModal(true)} style={styles.sideBtn}>🔒 Mot de passe</Button>
          <Button variant="sidebar" size="sm" onClick={handleLogout} style={styles.sideBtn}>Déconnexion</Button>
        </div>
      </aside>
      <main style={styles.main}>
        <Outlet />
      </main>

      {pwdModal && (
        <Modal title="Changer le mot de passe" width={420} onClose={closePwdModal} onSubmit={handleChangePassword}
               footer={<>
                 <Button onClick={closePwdModal}>{pwdOk ? 'Fermer' : 'Annuler'}</Button>
                 {!pwdOk && <Button type="submit" variant="primary">Modifier</Button>}
               </>}>
            {[
              { label: 'Mot de passe actuel', value: current, set: setCurrent },
              { label: 'Nouveau mot de passe (8 caractères min.)', value: next, set: setNext },
              { label: 'Confirmation du nouveau mot de passe', value: confirm, set: setConfirm },
            ].map(f => (
              <label key={f.label} style={styles.pwdLabel}>
                {f.label}
                <PasswordInput required value={f.value}
                       onChange={e => f.set(e.target.value)}
                       style={{ ...styles.pwdInput, marginTop: 0 }}
                       wrapperStyle={{ marginTop: '0.25rem' }} />
              </label>
            ))}
            {pwdError && <div style={styles.pwdError}>{pwdError}</div>}
            {pwdOk && <div style={styles.pwdSuccess}>Mot de passe modifié ✔</div>}
        </Modal>
      )}
    </div>
  );
}

// Charte Activus Group : bleu nuit #232853 (logo), lighter #6376aa, fond clair #f5f5f5,
// texte #444 — alignée sur le thème par défaut de Digibrain (styles.scss du front).
const styles = {
  // height 100vh + overflow hidden : seule la zone principale défile (verticalement
  // ET horizontalement dans les cartes) — la sidebar et ses boutons du bas restent
  // toujours visibles, quelle que soit la longueur des listes.
  shell: { display: 'flex', height: '100vh', overflow: 'hidden', background: '#f5f5f5', color: '#444444' },
  sidebar: { width: '220px', background: '#232853', display: 'flex', flexDirection: 'column', padding: '1.25rem 1rem', flexShrink: 0, overflowY: 'auto' },
  logoBox: { background: '#ffffff', borderRadius: '8px', padding: '0.6rem 0.75rem', marginBottom: '0.75rem', display: 'flex', justifyContent: 'center' },
  logoImg: { width: '100%', maxWidth: '160px', height: 'auto', display: 'block' },
  appName: { fontSize: '0.95rem', fontWeight: 700, color: '#ffffff', marginBottom: '1.75rem', textAlign: 'center', letterSpacing: '0.03em' },
  link: { display: 'block', padding: '0.6rem 0.75rem', marginBottom: '4px', borderRadius: '8px', color: '#b9c0dd', textDecoration: 'none', fontSize: '0.95rem', transition: 'all 0.15s' },
  active: { background: '#6376aa', color: '#fff' },
  userBlock: { marginTop: 'auto', borderTop: '1px solid #3c4373', paddingTop: '1rem' },
  userName: { display: 'block', color: '#b9c0dd', fontSize: '0.85rem', marginBottom: '0.5rem' },
  // Pleine largeur, empilés en bas de la barre latérale
  sideBtn: { width: '100%', marginBottom: '0.5rem' },
  main: { flex: 1, minWidth: 0, padding: '2rem', overflowY: 'auto' },
  pwdLabel: { display: 'block', color: '#6b7280', fontSize: '0.85rem', marginBottom: '0.75rem' },
  pwdInput: { width: '100%', marginTop: '0.25rem', background: '#f5f5f5', border: '1px solid #cdcdcd', color: '#444444', padding: '0.5rem', borderRadius: '6px', boxSizing: 'border-box' },
  pwdError: { color: '#c62828', fontSize: '0.85rem', marginTop: '0.25rem' },
  pwdSuccess: { color: '#1a7f37', fontSize: '0.85rem', marginTop: '0.25rem' },
};
